// src/components/NewVehicleRightSidebar.tsx

import { Box, Typography, Paper, Divider, Stack, Button, Grid } from '@mui/material';

// Existing Icons
import EventIcon from '@mui/icons-material/Event';
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';
import AirlineSeatReclineNormalIcon from '@mui/icons-material/AirlineSeatReclineNormal';
import EvStationIcon from '@mui/icons-material/EvStation';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import PhoneIcon from '@mui/icons-material/Phone';

// New Icons for the Spec List
import StorefrontIcon from '@mui/icons-material/Storefront';
import LanguageIcon from '@mui/icons-material/Language';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import SettingsIcon from '@mui/icons-material/Settings';
import EngineeringIcon from '@mui/icons-material/Engineering';
import SpeedIcon from '@mui/icons-material/Speed';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import OpacityIcon from '@mui/icons-material/Opacity';
import StraightenIcon from '@mui/icons-material/Straighten';
import ScaleIcon from '@mui/icons-material/Scale';
import InventoryIcon from '@mui/icons-material/Inventory';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CampaignIcon from '@mui/icons-material/Campaign';

import { NewVehicle, VehicleVariant } from '../types/NewVehicleTypes';

interface NewVehicleRightSidebarProps {
  vehicle: NewVehicle;
  selectedVariant: VehicleVariant;
  adImages?: string[]; // <-- New Prop added here
}

export default function NewVehicleRightSidebar({ 
  vehicle, 
  selectedVariant, 
  adImages = [] // Default to empty array
}: NewVehicleRightSidebarProps) {
  // Build the specs array to map over
  const specsList = [
    {
      icon: <StorefrontIcon sx={{ fontSize: 20 }} />,
      label: 'Distributor',
      value: vehicle.vehicleDealerInfo?.dealerName,
      valueColor: '#FFB800',
      subValue: vehicle.vehicleDealerInfo?.contactNumber,
      isPhone: true,
      hasChevron: true
    },
    {
      icon: <LanguageIcon sx={{ fontSize: 20 }} />,
      label: 'Built in',
      value: selectedVariant.countryOfOrigin,
    },
    {
      icon: <DirectionsCarIcon sx={{ fontSize: 20 }} />,
      label: 'Vehicle Type',
      value: selectedVariant.bodyType,
    },
    {
      icon: <SettingsIcon sx={{ fontSize: 20 }} />,
      label: 'Drive Type',
      value: selectedVariant.specGroup.driveType,
    },
    {
      icon: <LocalGasStationIcon sx={{ fontSize: 20 }} />,
      label: 'Fuel Type',
      value: selectedVariant.fuelType,
    },
    {
      icon: <EngineeringIcon sx={{ fontSize: 20 }} />,
      label: 'Engine',
      value: selectedVariant.specGroup.engineCode || selectedVariant.specGroup.engineType,
    },
    {
      icon: <SpeedIcon sx={{ fontSize: 20 }} />,
      label: 'Power',
      value: selectedVariant.specGroup.maximumPowerOutput
        ? `${selectedVariant.specGroup.maximumPowerOutput} kW${selectedVariant.specGroup.powerRPM ? ` @ ${selectedVariant.specGroup.powerRPM} rpm` : ''}`
        : null,
    },
    {
      icon: <AutorenewIcon sx={{ fontSize: 20 }} />,
      label: 'Torque',
      value: selectedVariant.specGroup.torque
        ? `${selectedVariant.specGroup.torque} Nm${selectedVariant.specGroup.torqueRPM ? ` @ ${selectedVariant.specGroup.torqueRPM} rpm` : ''}`
        : null,
    },
    {
      icon: <AccountTreeIcon sx={{ fontSize: 20 }} />,
      label: 'Transmission',
      value: selectedVariant.transmissionType,
    },
    {
      icon: <OpacityIcon sx={{ fontSize: 20 }} />,
      label: 'Fuel Tank Capacity',
      value: (selectedVariant.specGroup as any).fuelTankCapacity ? `${(selectedVariant.specGroup as any).fuelTankCapacity} L` : null,
    },
    {
      icon: <AirlineSeatReclineNormalIcon sx={{ fontSize: 20 }} />,
      label: 'Seating Capacity',
      value: selectedVariant.specGroup.seatCapacity ? `${selectedVariant.specGroup.seatCapacity} Seats` : null,
    },
    {
      icon: <StraightenIcon sx={{ fontSize: 20 }} />,
      label: 'Dimensions (LxWxH)',
      value: selectedVariant.specGroup.length ? `${selectedVariant.specGroup.length} x ${selectedVariant.specGroup.width} x ${selectedVariant.specGroup.height} mm` : null,
    },
    {
      icon: <ScaleIcon sx={{ fontSize: 20 }} />,
      label: 'Gross Vehicle Weight',
      value: (selectedVariant.specGroup as any).grossVehicleWeight ? `${(selectedVariant.specGroup as any).grossVehicleWeight} kg` : null,
    },
    {
      icon: <InventoryIcon sx={{ fontSize: 20 }} />,
      label: 'Stock Status',
      value: 'Ready Stock', 
      isPill: true
    }
  ];

  return (
    <Box sx={{ position: 'sticky', top: 100 }}>
      {/* Pricing & Quick Specs Card */}
      <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, mb: 3 }}>
        <Typography variant="subtitle2" color="text.secondary" fontWeight={600} mb={0.5}>
          Starting From
        </Typography>

        {selectedVariant.priceGroup.currentPrice != null ? (
          <Typography variant="h3" fontWeight={800} color="#FFB800" mb={0.5}>
            ${selectedVariant.priceGroup.currentPrice.toLocaleString()}
          </Typography>
        ) : (
          <Typography variant="h3" fontWeight={800} color="text.disabled" mb={0.5}>
            POA
          </Typography>
        )}

        {selectedVariant.priceGroup.monthlyInstalment != null && (
          <Typography variant="body2" color="text.secondary" fontWeight={500} mb={3}>
            From <b>${selectedVariant.priceGroup.monthlyInstalment.toLocaleString()} / month</b>
          </Typography>
        )}

        {/* 4 Icon Features */}
        <Box display="flex" justifyContent="space-between" textAlign="center" mb={3} px={1}>
          <Box>
            <EventIcon sx={{ color: 'text.secondary', mb: 0.5 }} />
            <Typography variant="body2" fontWeight={700}>{selectedVariant.modelYear || 'N/A'}</Typography>
            <Typography variant="caption" color="text.secondary" display="block">Model Year</Typography>
          </Box>
          <Box>
            {selectedVariant.fuelType.toLowerCase() === 'electric' ? (
              <EvStationIcon sx={{ color: 'text.secondary', mb: 0.5 }} />
            ) : (
              <LocalGasStationIcon sx={{ color: 'text.secondary', mb: 0.5 }} />
            )}
            <Typography variant="body2" fontWeight={700}>
              {selectedVariant.specGroup.engineCapacity || selectedVariant.fuelType}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">Engine</Typography>
          </Box>
          <Box>
            <SettingsSuggestIcon sx={{ color: 'text.secondary', mb: 0.5 }} />
            <Typography variant="body2" fontWeight={700}>
              {selectedVariant.transmissionType === 'Automatic' ? 'Auto' : 'Manual'}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">Transmission</Typography>
          </Box>
          <Box>
            <AirlineSeatReclineNormalIcon sx={{ color: 'text.secondary', mb: 0.5 }} />
            <Typography variant="body2" fontWeight={700}>
              {selectedVariant.specGroup.seatCapacity || '-'} Seats
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">Seating</Typography>
          </Box>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Detailed Specs List */}
        <Stack spacing={2} mb={4}>
          {specsList
            .filter((item) => item.value)
            .map((spec, idx) => (
              <Box key={idx} display="flex" alignItems="flex-start">
                
                {/* Icon & Label */}
                <Box display="flex" alignItems="center" sx={{ width: '45%', flexShrink: 0 }}>
                  <Box sx={{ color: 'text.secondary', display: 'flex', mr: 1.5 }}>
                    {spec.icon}
                  </Box>
                  <Typography variant="body2" fontWeight={700} color="text.primary">
                    {spec.label}
                  </Typography>
                </Box>

                {/* Value Column */}
                <Box sx={{ flexGrow: 1 }}>
                  {spec.isPill ? (
                    <Box
                      sx={{
                        display: 'inline-block',
                        bgcolor: '#e6f4ea',
                        color: '#137333',
                        px: 1.2,
                        py: 0.4,
                        borderRadius: 1.5,
                        typography: 'caption',
                        fontWeight: 700
                      }}
                    >
                      {spec.value}
                    </Box>
                  ) : (
                    <Typography 
                      variant="body2" 
                      fontWeight={600} 
                      sx={{ 
                        color: spec.valueColor || 'text.secondary', 
                        display: 'flex', 
                        alignItems: 'center' 
                      }}
                    >
                      {spec.value}
                      {spec.hasChevron && <ChevronRightIcon sx={{ fontSize: 16, ml: 0.5 }} />}
                    </Typography>
                  )}

                  {/* SubValue */}
                  {spec.subValue && spec.isPhone && (
                    <Box display="flex" alignItems="center" mt={0.5} sx={{ color: 'text.primary' }}>
                      <PhoneIcon sx={{ fontSize: 16, mr: 0.5 }} />
                      <Typography variant="body2" fontWeight={700}>
                        {spec.subValue}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            ))}
        </Stack>

        {/* Action Buttons */}
        <Button
          variant="contained"
          fullWidth
          sx={{ py: 1.5, mb: 1.5, bgcolor: '#FFB800', color: '#000', fontWeight: 800, fontSize: 16, '&:hover': { bgcolor: '#e5a600' } }}
        >
          Get Best Offer
        </Button>

        <Grid container spacing={1.5}>
          <Grid size={{ xs: 6 }}>
            <Button
              variant="outlined"
              fullWidth
              startIcon={<WhatsAppIcon sx={{ color: '#25D366' }} />}
              sx={{ py: 1, color: 'text.primary', borderColor: '#e0e0e0', fontWeight: 600 }}
              href={`https://wa.me/${vehicle.vehicleDealerInfo?.contactNumber?.replace(/\D/g, '')}`}
              target="_blank"
            >
              WhatsApp
            </Button>
          </Grid>
          <Grid size={{ xs: 6 }}>
            <Button
              variant="outlined"
              fullWidth
              startIcon={<PhoneIcon sx={{ color: '#FFB800' }} />}
              sx={{ py: 1, color: 'text.primary', borderColor: '#e0e0e0', fontWeight: 600 }}
              href={`tel:${vehicle.vehicleDealerInfo?.contactNumber?.replace(/\D/g, '')}`}
            >
              Call Dealer
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Dynamic Promo Text (Restored & Compacted) */}
      {(vehicle.promoTitle || vehicle.promoOffer) && (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 3, bgcolor: '#fffdf5', borderColor: '#ffe082' }}>
          <Stack direction="row" alignItems="flex-start" spacing={1}>
            <CampaignIcon sx={{ color: '#f59e0b', fontSize: 20, mt: 0.2 }} />
            <Box>
              <Typography variant="subtitle2" fontWeight={800} color="text.primary" lineHeight={1.2} mb={0.5}>
                {vehicle.promoTitle}
              </Typography>
              {vehicle.promoCallout && (
                <Typography variant="caption" fontWeight={800} color="#d32f2f" display="block" mb={0.5}>
                  {vehicle.promoCallout}
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.4 }}>
                {vehicle.promoOffer} {vehicle.promoSubtext}
              </Typography>
            </Box>
          </Stack>
        </Paper>
      )}

    <Box sx={{ width: '100%', mb: 4 }}>
        {/* Subtle Advertisement Label */}
        <Typography 
          variant="caption" 
          sx={{ 
            display: 'block', 
            textAlign: 'center', 
            color: 'text.disabled', 
            letterSpacing: 1.5,
            mb: 1,
            fontSize: '0.65rem',
            textTransform: 'uppercase'
          }}
        >
          - Advertisement -
        </Typography>

        {adImages.length > 0 ? (
          <Stack spacing={2}>
            {adImages.map((imgUrl, index) => (
              <Box
                key={index}
                component="img"
                src={imgUrl}
                alt={`Advertisement ${index + 1}`}
                sx={{
                  width: '100%',
                  aspectRatio: '1 / 1', // Forces a perfect square box
                  objectFit: 'cover',   // Prevents image distortion
                  borderRadius: 2,
                  display: 'block',
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 16px rgba(0,0,0,0.12)'
                  }
                }}
              />
            ))}
          </Stack>
        ) : (
          <Paper
            elevation={0}
            sx={{
              width: '100%',
              aspectRatio: '1 / 1', // Matches the square box shape for the empty state
              bgcolor: '#f8f9fa',
              border: '1px dashed #e0e0e0',
              borderRadius: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'text.secondary',
            }}
          >
            <CampaignIcon sx={{ fontSize: 40, mb: 1, opacity: 0.4 }} />
            <Typography variant="body2" fontWeight={600} color="text.secondary">
              Ad Space Available
            </Typography>
          </Paper>
        )}
      </Box>
    </Box>
  );
}